// DocenteCuestionarios — Vista del docente para agendar y monitorear los
// cuestionarios CEP/CAD de sus estudiantes. También accesible para
// franquiciado/admin (con scope ampliado).
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { formatDateMX, todayMX } from "@/lib/dates";
import { Pagination } from "@/components/Pagination";
import { toast } from "sonner";
import {
  Calendar, Clock, Send, CheckCircle2, X, AlertCircle, ChevronRight, BarChart3, FileText,
} from "lucide-react";

const STATUS_COLORS = {
  pending: { bg: "#FFF7DA", text: "#9C7A18", label: "Pendiente" },
  in_progress: { bg: "#E8EEFB", text: "#2C49A0", label: "En curso" },
  completed: { bg: "#E7F4E9", text: "#2A7A36", label: "Completado" },
  cancelled: { bg: "#F2F4FA", text: "#6E7591", label: "Cancelado" },
};

// Etapa CEP/CAD en español.
const STAGE_LABELS = {
  initial: "Inicial",
  intermediate: "Intermedio",
  final: "Final",
};
const stageLabel = (s) => (s ? (STAGE_LABELS[String(s).toLowerCase()] || s) : "—");

// Mínimo de fecha permitido para agendar (hoy en zona local).
const todayLocalISO = () => todayMX();

// v5.5.0 · «Asignar diagnósticos» y «Resultados de diagnósticos» son módulos
// distintos (cada uno con su ruta): `mode` decide cuál se muestra.
export default function DocenteCuestionarios({ mode = "assignments", otherTo = null, otherLabel = "" }) {
  const { user } = useAuth();
  const tab = mode === "responses" ? "responses" : "assignments"; // assignments | responses
  const [students, setStudents] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [responses, setResponses] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [creating, setCreating] = useState(false);
  const [viewingResponse, setViewingResponse] = useState(null);
  const [editing, setEditing] = useState(null);

  const reload = async () => {
    try {
      const [a, r, t, s] = await Promise.all([
        api.get("/questionnaire-assignments"),
        api.get("/questionnaire-responses"),
        api.get("/questionnaire-templates"),
        api.get("/students"),
      ]);
      setAssignments(a.data);
      setResponses(r.data);
      setTemplates(t.data);
      setStudents(s.data);
    } catch (e) {
      // sin estudiantes asignados no es error fatal
    }
  };
  useEffect(() => { reload(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-6" data-testid="docente-cuestionarios">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs tracking-[0.18em] uppercase font-bold text-[#2C49A0]">Módulo M2 · M3</p>
          <h1 className="font-display text-3xl md:text-4xl font-extrabold text-[#1F2547]" data-testid="docente-cuest-title">
            {tab === "assignments" ? "Asignar diagnósticos" : "Resultados de diagnósticos"}
          </h1>
          <p className="text-sm text-[#6E7591] mt-1">
            {tab === "assignments"
              ? "Programa la aplicación de los diagnósticos CEP y CAD (inicial, intermedia y final) de tus estudiantes."
              : "Consulta el detalle de las respuestas registradas por etapa."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {otherTo && (
            <Link to={otherTo} data-testid="cuest-other-module"
              className="jasq-btn-ghost text-xs inline-flex items-center gap-1.5">
              {otherLabel} <ChevronRight size={12} />
            </Link>
          )}
          {tab === "assignments" && (
            <button onClick={() => setCreating(true)} className="jasq-btn-primary inline-flex items-center gap-2"
              data-testid="open-create-assignment">
              <Send size={14} /> Agendar cuestionario
            </button>
          )}
        </div>
      </div>

      {tab === "assignments" && (
        <AssignmentList assignments={assignments} onReload={reload} userRole={user?.role} onEdit={setEditing} />
      )}
      {tab === "responses" && (
        <ResponseList responses={responses} onView={setViewingResponse} />
      )}

      {creating && (
        <CreateAssignmentModal
          students={students}
          templates={templates.filter((t) => t.active)}
          onClose={() => setCreating(false)}
          onSaved={() => { setCreating(false); reload(); }}
        />
      )}
      {editing && (
        <EditAssignmentModal
          assignment={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); reload(); }}
        />
      )}
      {viewingResponse && (
        <ResponseDetailModal response={viewingResponse} onClose={() => setViewingResponse(null)} />
      )}
    </div>
  );
}

function AssignmentList({ assignments, onReload, userRole, onEdit }) {
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;

  const counts = useMemo(() => {
    const c = { all: assignments.length, pending: 0, in_progress: 0, completed: 0 };
    assignments.forEach((a) => { c[a.status] = (c[a.status] || 0) + 1; });
    return c;
  }, [assignments]);

  const filtered = statusFilter === "all" ? assignments : assignments.filter((a) => a.status === statusFilter);
  useEffect(() => { setPage(1); }, [statusFilter]);
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const cancel = async (id) => {
    if (!confirm("¿Eliminar esta asignación? Desaparecerá del listado.")) return;
    try {
      await api.delete(`/questionnaire-assignments/${id}`);
      toast.success("Asignación eliminada");
      onReload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const FILTERS = [
    { id: "all",         label: "Todos",      color: "#1F2547" },
    { id: "pending",     label: "Pendientes", color: "#9C7A18" },
    { id: "in_progress", label: "En curso",   color: "#2C49A0" },
    { id: "completed",   label: "Completados", color: "#2A7A36" },
  ];

  return (
    <div className="space-y-3" data-testid="assignment-list">
      <div className="flex flex-wrap items-center gap-2" data-testid="assignment-status-filter">
        <span className="text-[11px] uppercase tracking-wider font-extrabold text-[#6E7591]">Filtrar por estado:</span>
        {FILTERS.map((f) => (
          <button key={f.id} onClick={() => setStatusFilter(f.id)}
            data-testid={`assignment-filter-${f.id}`}
            className={`text-xs font-bold px-3 py-1.5 rounded-full border-2 transition-colors ${statusFilter === f.id ? "bg-[#1F2547] border-[#1F2547] text-white" : "bg-white border-[#E1E5F0] text-[#4C5478] hover:border-[#1F2547]"}`}>
            {f.label} <span className="opacity-70 ml-1">({counts[f.id] || 0})</span>
          </button>
        ))}
      </div>

      <div className="jasq-card overflow-hidden">
        <table className="w-full">
          <thead className="bg-[#F7F8FA] border-b-2 border-[#E1E5F0]">
            <tr>
              <Th>Estudiante</Th><Th>Cuestionario</Th><Th>Etapa</Th><Th>Fecha programada</Th><Th>Estado</Th><Th>Acciones</Th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={6} className="px-5 py-10 text-center text-sm text-[#6E7591]">
                {statusFilter === "all"
                  ? "Aún no has agendado ningún cuestionario."
                  : `Sin cuestionarios en estado "${FILTERS.find((f) => f.id === statusFilter)?.label}".`}
              </td></tr>
            )}
            {paged.map((a) => {
              const s = STATUS_COLORS[a.status] || STATUS_COLORS.pending;
              return (
                <tr key={a.id} className="border-b border-[#E1E5F0] last:border-0 hover:bg-[#FAFBFE]" data-testid={`assignment-row-${a.id}`}>
                  <Td className="font-bold text-[#1F2547]">{a.student_name || a.student_id}</Td>
                  <Td>{a.template_title} <span className="text-[#A43790] font-bold">[{a.template_code}]</span></Td>
                  <Td>{stageLabel(a.stage)}</Td>
                  <Td>{a.scheduled_for ? formatDateMX(a.scheduled_for) : <span className="text-[#6E7591]">Sin fecha</span>}</Td>
                  <Td>
                    <span className="jasq-pill text-xs" style={{ background: s.bg, color: s.text }}>{s.label}</span>
                  </Td>
                  <Td>
                    <div className="flex items-center gap-3">
                      {a.status === "pending" && (
                        <button
                          onClick={() => onEdit(a)}
                          className="text-xs font-bold text-[#2C49A0] hover:underline"
                          data-testid={`edit-${a.id}`}
                        >
                          Editar
                        </button>
                      )}
                      {a.status === "pending" && (
                        <button onClick={() => cancel(a.id)} className="text-xs font-bold text-[#A52258] hover:underline" data-testid={`cancel-${a.id}`}>
                          Eliminar
                        </button>
                      )}
                      {a.status !== "pending" && (
                        <span className="text-[11px] text-[#6E7591] italic" data-testid={`locked-${a.id}`}>
                          {a.status === "in_progress" ? "Bloqueado · en curso" : "Bloqueado · completado"}
                        </span>
                      )}
                    </div>
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <Pagination page={page} pageSize={PAGE_SIZE} total={filtered.length} onPageChange={setPage} testid="assignments-pagination" />
      </div>
    </div>
  );
}

function ResponseList({ responses, onView }) {
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;
  const paged = responses.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  return (
    <div className="jasq-card overflow-hidden" data-testid="response-list">
      <table className="w-full">
        <thead className="bg-[#F7F8FA] border-b-2 border-[#E1E5F0]">
          <tr>
            <Th>Estudiante</Th><Th>Cuestionario</Th><Th>Etapa</Th><Th>Estado</Th><Th>Respuestas</Th><Th>Fecha</Th><Th></Th>
          </tr>
        </thead>
        <tbody>
          {responses.length === 0 && (
            <tr><td colSpan={7} className="px-5 py-10 text-center text-sm text-[#6E7591]">
              No hay respuestas registradas todavía.
            </td></tr>
          )}
          {paged.map((r) => (
            <tr key={r.id} className="border-b border-[#E1E5F0] last:border-0 hover:bg-[#FAFBFE]" data-testid={`response-row-${r.id}`}>
              <Td className="font-bold text-[#1F2547]">{r.student_name || r.student_id}</Td>
              <Td>{r.template_code}</Td>
              <Td>{stageLabel(r.stage)}</Td>
              <Td><b className="text-[#3FA84D]">Realizado</b></Td>
              <Td>{r.answered_count ?? "—"}</Td>
              <Td>{r.completed_at ? new Date(r.completed_at).toLocaleString("es-MX") : "—"}</Td>
              <Td>
                <button onClick={() => onView(r)} className="text-xs font-bold text-[#2C49A0] hover:underline inline-flex items-center gap-1"
                  data-testid={`view-response-${r.id}`}>
                  <BarChart3 size={12} /> Ver detalle <ChevronRight size={11} />
                </button>
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
      <Pagination page={page} pageSize={PAGE_SIZE} total={responses.length} onPageChange={setPage} testid="responses-pagination" />
    </div>
  );
}

function CreateAssignmentModal({ students, templates, onClose, onSaved }) {
  const [studentId, setStudentId] = useState("");
  const [studentQ, setStudentQ] = useState("");
  const sortedStudents = useMemo(() => {
    const norm = (x) => (x || "").toString().normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
    const q = norm(studentQ.trim());
    return [...students]
      .filter((s) => !q || norm(s.name).includes(q) || norm(s.email).includes(q))
      .sort((a, b) => (a.name || "").localeCompare(b.name || "", "es", { sensitivity: "base" }));
  }, [students, studentQ]);
  const [code, setCode] = useState("CEP2");
  const [date, setDate] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!studentId || !code) return;
    setSubmitting(true);
    try {
      await api.post("/questionnaire-assignments", {
        student_id: studentId,
        template_code: code,
        scheduled_for: date || null,
        notes: notes || null,
      });
      toast.success("Cuestionario agendado");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al agendar");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#1F2547]/50 backdrop-blur-sm flex justify-end" data-testid="create-assignment-modal">
      <div className="jasq-drawer-panel bg-white shadow-2xl w-full max-w-md h-full overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E1E5F0]">
          <h3 className="font-display text-xl font-extrabold text-[#1F2547]">Agendar cuestionario</h3>
          <button onClick={onClose} className="text-[#6E7591] hover:text-[#1F2547]"><X size={20} /></button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <Field label="Estudiante">
            <input className="jasq-input mb-2" placeholder="Buscar estudiante por nombre o correo…" value={studentQ}
              onChange={(e) => setStudentQ(e.target.value)} data-testid="ca-student-search" />
            <select className="jasq-input" value={studentId} onChange={(e) => setStudentId(e.target.value)} data-testid="ca-student">
              <option value="">{sortedStudents.length ? `Selecciona… (${sortedStudents.length}, A-Z)` : "Sin coincidencias"}</option>
              {sortedStudents.map((s) => <option key={s.id} value={s.id}>{s.name} · {s.grade || "—"}</option>)}
            </select>
          </Field>
          <Field label="Cuestionario">
            <select className="jasq-input" value={code} onChange={(e) => setCode(e.target.value)} data-testid="ca-template">
              {templates.map((t) => (
                <option key={t.code} value={t.code}>{t.code} · {t.title}</option>
              ))}
            </select>
          </Field>
          <Field label="Fecha programada (opcional)">
            <input type="date" min={todayLocalISO()} className="jasq-input" value={date} onChange={(e) => setDate(e.target.value)} data-testid="ca-date" />
          </Field>
          <Field label="Notas (opcional)">
            <textarea rows={2} className="jasq-input" value={notes} onChange={(e) => setNotes(e.target.value)} />
          </Field>
        </div>
        <div className="px-6 py-4 border-t border-[#E1E5F0] bg-[#F7F8FA] flex items-center gap-3">
          <button onClick={onClose} className="jasq-btn-ghost">Cancelar</button>
          <div className="flex-1" />
          <button onClick={submit} disabled={!studentId || submitting} className="jasq-btn-primary inline-flex items-center gap-2" data-testid="ca-submit">
            <Send size={14} /> {submitting ? "Agendando…" : "Agendar"}
          </button>
        </div>
      </div>
    </div>
  );
}

function EditAssignmentModal({ assignment, onClose, onSaved }) {
  // Permite editar la fecha programada y/o las notas. La fecha solo puede
  // ser hoy o futura.
  const initial = assignment.scheduled_for
    ? String(assignment.scheduled_for).slice(0, 10)
    : "";
  const [date, setDate] = useState(initial);
  const [notes, setNotes] = useState(assignment.notes || "");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (date && date < todayLocalISO()) {
      toast.error("La fecha no puede ser anterior al día de hoy.");
      return;
    }
    setSubmitting(true);
    try {
      await api.patch(`/questionnaire-assignments/${assignment.id}`, {
        scheduled_for: date || null,
        notes: notes || null,
      });
      toast.success("Asignación actualizada");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al actualizar");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#1F2547]/50 backdrop-blur-sm flex justify-end" data-testid="edit-assignment-modal">
      <div className="jasq-drawer-panel bg-white shadow-2xl w-full max-w-md h-full overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E1E5F0]">
          <div>
            <p className="text-[11px] uppercase tracking-wider font-extrabold text-[#A43790]">Editar asignación</p>
            <h3 className="font-display text-xl font-extrabold text-[#1F2547]">{assignment.student_name}</h3>
          </div>
          <button onClick={onClose} className="text-[#6E7591] hover:text-[#1F2547]"><X size={20} /></button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div className="rounded-xl bg-[#F7F8FA] border border-[#E1E5F0] px-3 py-2 text-xs text-[#4C5478]">
            <b>{assignment.template_title}</b> <span className="text-[#A43790] font-bold">[{assignment.template_code}]</span>
            {assignment.stage && <span className="text-[#6E7591]"> · Etapa: {stageLabel(assignment.stage)}</span>}
          </div>
          <Field label="Fecha programada">
            <input type="date" min={todayLocalISO()} className="jasq-input" value={date} onChange={(e) => setDate(e.target.value)} data-testid="edit-date" />
            <p className="text-[11px] text-[#6E7591] mt-1">Deja vacío si no quieres fijar fecha aún.</p>
          </Field>
          <Field label="Notas (opcional)">
            <textarea rows={3} className="jasq-input" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="edit-notes" />
          </Field>
        </div>
        <div className="px-6 py-4 border-t border-[#E1E5F0] bg-[#F7F8FA] flex items-center gap-3">
          <button onClick={onClose} className="jasq-btn-ghost">Cancelar</button>
          <div className="flex-1" />
          <button onClick={submit} disabled={submitting} className="jasq-btn-primary inline-flex items-center gap-2" data-testid="edit-submit">
            <Send size={14} /> {submitting ? "Guardando…" : "Guardar cambios"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ResponseDetailModal({ response, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-[#1F2547]/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="response-detail-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl overflow-hidden flex flex-col max-h-[92vh]">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E1E5F0]">
          <div>
            <p className="text-[11px] uppercase tracking-wider font-extrabold text-[#A43790]">{response.template_code} · {stageLabel(response.stage)}</p>
            <h3 className="font-display text-xl font-extrabold text-[#1F2547]">Detalle de respuesta · {response.student_name}</h3>
          </div>
          <button onClick={onClose} className="text-[#6E7591] hover:text-[#1F2547]"><X size={20} /></button>
        </div>
        <div className="px-6 py-5 space-y-5 overflow-y-auto min-h-0">
          <div className="grid sm:grid-cols-3 gap-3">
            <Stat label="Estado" value="Realizado" accent="#3FA84D" />
            <Stat label="Respondidas" value={response.answered_count} accent="#2C49A0" />
          </div>

          <div>
            <h4 className="font-bold text-sm text-[#1F2547] mb-2">Puntaje por bloque</h4>
            <div className="space-y-2">
              {(response.block_scores || []).map((b) => (
                <div key={b.block_id} className="rounded-xl border border-[#E1E5F0] p-3">
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-sm font-bold text-[#1F2547] truncate">{b.title}</p>
                    <div className="text-xs text-[#6E7591] font-bold whitespace-nowrap">
                      Σ {b.sum} · ⌀ {b.avg ?? "—"} · n={b.count}
                    </div>
                  </div>
                  <div className="mt-2 h-2 bg-[#F2F4FA] rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-[#FFCC28] to-[#2C49A0]"
                      style={{ width: `${Math.min(100, ((b.avg || 0) / 5) * 100)}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {response.teacher_observations && (
            <div className="rounded-xl border-2 border-[#FFCC28] bg-[#FFF7DA] p-3">
              <p className="text-[11px] uppercase tracking-wider font-extrabold text-[#A43790]">Observaciones del docente</p>
              <p className="text-sm text-[#1F2547] mt-1">{response.teacher_observations}</p>
            </div>
          )}

          <div>
            <h4 className="font-bold text-sm text-[#1F2547] mb-2">Respuestas individuales</h4>
            <div className="space-y-1.5 max-h-[320px] overflow-y-auto pr-2">
              {(response.answers || []).map((a) => (
                <AnswerRow key={a.question_id} a={a} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function AnswerRow({ a }) {
  const v = a.value;
  const isMath = v && typeof v === "object" && ("latex" in v || "photo_url" in v);
  return (
    <div className="text-xs px-3 py-2 rounded-lg bg-[#F7F8FA] flex items-start gap-3" data-testid={`answer-row-${a.question_id}`}>
      <span className="font-mono text-[10px] text-[#6E7591] shrink-0 mt-0.5">{a.question_id}</span>
      <div className="flex-1 text-[#1F2547] space-y-1.5 min-w-0">
        {isMath ? (
          <>
            {v.latex && (
              <p className="font-mono text-[11px] break-all bg-white border border-[#E1E5F0] rounded px-2 py-1">{v.latex}</p>
            )}
            {v.photo_url && (
              <a href={v.photo_url} target="_blank" rel="noreferrer" className="inline-block">
                <img src={v.photo_url} alt="Proceso del estudiante" className="max-h-28 rounded border-2 border-[#E1E5F0] hover:border-[#A43790]" />
              </a>
            )}
            {!v.latex && !v.photo_url && <span className="italic text-[#6E7591]">—</span>}
          </>
        ) : (
          <span>{String(v ?? "—")}</span>
        )}
      </div>
      <span className="font-bold text-[#2C49A0] shrink-0">
        {a.score != null ? `puntaje ${a.score}${a.inverted ? " (inv)" : ""}` : ""}
      </span>
    </div>
  );
}


function Stat({ label, value, accent }) {
  return (
    <div className="rounded-xl border-2 border-[#E1E5F0] p-3 text-center">
      <p className="text-[10px] uppercase tracking-wider font-extrabold text-[#6E7591]">{label}</p>
      <p className="text-2xl font-display font-extrabold mt-1" style={{ color: accent }}>{value ?? "—"}</p>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[11px] tracking-wider uppercase font-extrabold text-[#6E7591]">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

function Th({ children }) {
  return <th className="text-left text-[11px] tracking-wider uppercase font-extrabold text-[#6E7591] px-5 py-3">{children}</th>;
}
function Td({ children, className = "" }) {
  return <td className={`px-5 py-3 text-sm text-[#4C5478] ${className}`}>{children}</td>;
}
